<script setup>
import {reactive, onMounted, onUnmounted, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'PayPeriodPersonUnreleasedComponent';

const props = defineProps({
  contextSchemaA: String,
  contextSchemaB: String,
  contextIdA: String,
  contextIdB: String
});

const form = ref(null);

const data = reactive({
  defaultItem: {
    amount: null,
    description: null
  },
  contextSchemaB: null,
  headers: [
    { title: 'Claim Number', key: 'claimnumber' },
    { title: 'Created Date', key: 'date' },
    { title: 'Type', key: 'status' },
    { title: 'Amount', key: 'amount' },
    { title: 'Description', key: 'description' },
    { title: 'Released Date', key: 'released_date' },
    { title: 'Created By', key: 'created_by' },
    { title: 'Actions', key: 'actions', sortable: false }
  ],
  items: [],
  isDisabled: false,
  loading: false,
  newItem: {
    amount: null,
    description: null
  },
  openHoldbacksForm: false,
  search: null,
  valid: true
})

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteMessage === true) {
    data.loading = true
    data.openHoldbacksForm = false
     ApiWrapper.post('holdbacks', 'delete-holdback', {
          id: item.id
        })
        .then((response) => {
          getData()
          activeComponentStore.notifyComponents(['holdbacks'],compName);
          ToastUtil.successMsg('Deleted holdback');
        })
         .catch((err) => {
           ToastUtil.error(err);
           data.loading = false;
         })
  }
}

const discardItem = () => {
  data.openHoldbacksForm = false
  data.newItem = Object.assign({}, data.defaultItem)
  if (data.isEditing) {
    data.getData()
  }
  data.isEditing = false
}

const editItem = (item) => {
  data.isEditing = true
  data.openHoldbacksForm = true
  data.newItem = JSON.parse(JSON.stringify(item))
}

const formatISODateToAmerican = (date) => {
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const getData = () => {
  data.loading = true
  let contextIdA = props.contextIdA;
  let contextIdB = props.contextIdB;
  let contextSchemaB = props.contextSchemaB;


   ApiWrapper.get(
          'holdbacks',
          'pay-period-person','payPeriodId=' +
          contextIdA +
          '&personId=' +
          contextIdB
      )
      .then((response) => {
        if(response.holdbacks){
          let temp = []
          for(let x in response.holdbacks){
            if(response.holdbacks[x].status === 'Active'){
              temp.push(response.holdbacks[x])
            }
          }
          data.items = temp
        }
      })
       .catch((e)=>{
         ToastUtil.error(e);
       })
     .finally(()=>{
       data.loading = false
       data.isDisabled = false
     })
}

const releaseHoldback = (item) => {
  data.loading = true
   ApiWrapper.post('holdbacks', 'release-holdback-into-pay-period', {
        holdbackId: item.id,
        payPeriodId: props.contextIdA
      })
      .then((response) => {
        getData()
        activeComponentStore.notifyComponents(['holdbacks'],compName);
        ToastUtil.successMsg('Released holdback into pay period');
      })
       .catch((err) => {
         ToastUtil.error(err);
         data.loading = false;
       })
}

const saveItem = () => {
  if (form.value.validate()) {
    data.openHoldbacksForm = false
    data.isDisabled = true
    data.loading = true
    if (data.isEditing) {
       ApiWrapper.post('holdbacks', 'edit-holdback', {
            editedItem: data.newItem
          })
          .then((response) => {
            getData()
            discardItem()
            ToastUtil.successMsg('Updated holdback');
          })
           .catch((err) => {
             ToastUtil.error(err);
             data.loading = false;
             data.isDisabled = false;
           })
    } else {
       ApiWrapper.post('holdbacks', 'create', {
            newHoldback: data.newItem,
            payPeriodId: props.contextIdA,
            personId: props.contextIdB
          })
          .then((response) => {
            getData()
            discardItem()
            activeComponentStore.notifyComponents(['holdbacks'],compName);
            ToastUtil.successMsg('Created holdback');
          })
           .catch((err) => {
             ToastUtil.error(err);
             data.loading = false;
             data.isDisabled = false;
           });
    }
  }
}

onMounted(()=>{
  activeComponentStore.registerComponent(
      compName,
      [
        'holdbacks'
      ],
      getData
  );
  getData()
})

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});


</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Unreleased Holdbacks Created In This Pay Period</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-spacer></v-spacer>
        <v-btn
            variant="elevated"
            color="primary"
            size="small"
            v-if="data.contextSchemaB != 'mypayperiod' && Permissions.checkPermissions(['holdbacks.create'])"
            @click="data.openHoldbacksForm = true"
        >
          New Holdback
        </v-btn>
      </v-toolbar>
      <v-form
          v-if="data.openHoldbacksForm"
          ref="form"
          v-model="data.valid"
          lazy-validation
      >
        <v-card-title v-if="!data.isEditing" class="primary--text">New Holdback</v-card-title>
        <v-card-title v-if="data.isEditing" class="primary--text">Edit Holdback</v-card-title>
        <v-row class="px-5">
          <v-col cols="12" sm="3">
            <v-text-field
                type="number"
                v-model="data.newItem.amount"
                label="Amount"
                :rules="[v => !!v || 'Amount is required']"
                prepend-inner-icon="mdi-currency-usd"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
                type="text"
                v-model="data.newItem.description"
                label="Description"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-btn size="small" variant="elevated" class="px-2" @click="saveItem" color="primary" :disabled="!data.valid || data.isDisabled === true">
              <v-icon color="white">mdi-plus</v-icon>
            </v-btn>
            <v-btn size="small" variant="elevated" class="px-2" @click="discardItem" color="error" :disabled="data.isDisabled === true">
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <v-row>
        <v-col cols="6">
          <v-text-field
              v-model="data.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              class="ml-4"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-data-table
          :items="data.items"
          :headers="data.headers"
          :search="data.search"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.claimnumber`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`"> {{ item.claimnumber }}</RouterLink>
        </template>

        <template v-slot:[`item.date`]="{ item }">
          {{ formatISODateToAmerican(item.date) }}
        </template>
        <template v-slot:[`item.amount`]="{ item }">
          {{ '$' + item.amount }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="Permissions.checkPermissions(['holdbacks.update'])" class="mr-2" @click="editItem(item)">mdi-pencil </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['holdbacks.delete'])" @click="deleteItem(item)">mdi-delete </v-icon>


        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
</style>